import { useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import Swal from 'sweetalert2';
import { userRequestService } from "../../../../services/userRequestService";
import { projectService } from "../../../../services/projectService";
import { guardianAreaService } from "../../../../services/guardianAreaService";
import {
  MainContainer,
  BackgroundPattern,
  ContentWrapper,
  ButtonContainerLeft,
  StyledButton,
  HeaderSection,
  Title,
  Subtitle,
  UserInfoCard,
  InfoText,
  SettingsCard,
  StyledLabel,
  StyledSelect,
  ButtonGroup,
  Row,
  LeftColumn,
  RightColumn,
  Label,
  TagsContainer,
  Tag,
  RemoveTagButton
} from "./styles";

const ROLE_LABELS = {
  TEAM: 'Equipe',
  MANAGER: 'Gestor'
};

const USER_TYPE_LABELS = {
  GUARDIAN: 'Guardian',
  PROP: 'Proprietário',
};

const MAX_AREAS = 5;

const areaProjectId = (area) => area.project_detail?.id ?? area.project_id ?? area.project;

const displayValue = (value) => {
  if (value === null || value === undefined || value === '') return 'Não informado';
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
  return value;
};

const AddUserRequest = ({ requestUser }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const userFromState = location.state?.requestUser;
  const user = requestUser || userFromState;
  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [userData, setUserData] = useState(null);
  const [userType, setUserType] = useState('GUARDIAN');
  const [permissions, setPermissions] = useState({
    project: "",
    areas: [],          // [{ id_code, name, role }]
    selectedArea: '',
    selectedRole: 'TEAM'
  });

  const [options, setOptions] = useState({
    projects: [],
    guardian_areas: [],
  });
  const [suggestionNote, setSuggestionNote] = useState('');
  const prefilledRef = useRef(false);

  useEffect(() => {
    if (user) {
      setUserData(user);
      const initialType = user.user_type === 'PROP' ? 'PROP' : 'GUARDIAN';
      setUserType(initialType);
    } else {
      navigate('/user_request');
    }
  }, [user, navigate]);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        setLoadingOptions(true);

        // Auth via cookie (mesmo padrão de CreateArea / FormNewAlert)
        const [projectsResponse, guardianAreasResponse] = await Promise.all([
          projectService.getProjects(),
          guardianAreaService.getGuardianAreas(),
        ]);

        let projects = [];
        if (Array.isArray(projectsResponse?.projects_project)) {
          projects = projectsResponse.projects_project;
        } else if (Array.isArray(projectsResponse?.data?.projects_project)) {
          projects = projectsResponse.data.projects_project;
        } else if (Array.isArray(projectsResponse)) {
          projects = projectsResponse;
        } else if (Array.isArray(projectsResponse?.data)) {
          projects = projectsResponse.data;
        }

        let areas = [];
        if (Array.isArray(guardianAreasResponse)) {
          areas = guardianAreasResponse;
        } else if (Array.isArray(guardianAreasResponse?.data)) {
          areas = guardianAreasResponse.data;
        } else if (Array.isArray(guardianAreasResponse?.guardian_areas)) {
          areas = guardianAreasResponse.guardian_areas;
        } else if (Array.isArray(guardianAreasResponse?.data?.guardian_areas)) {
          areas = guardianAreasResponse.data.guardian_areas;
        }

        setOptions({
          projects,
          guardian_areas: areas,
        });
      } catch (error) {
        console.error("Erro ao carregar opções:", error);
        setOptions({
          projects: [],
          guardian_areas: []
        });
        Swal.fire({
          title: 'Erro',
          text: 'Não foi possível carregar projetos e áreas. Tente novamente.',
          icon: 'error',
          background: 'var(--surface-overlay)',
          color: 'var(--accent-secondary)',
          confirmButtonColor: 'var(--danger)'
        });
      } finally {
        setLoadingOptions(false);
      }
    };

    fetchOptions();
  }, []);

  // Áreas sugeridas pelo proprietário (convite pelo app) vêm pré-preenchidas e
  // continuam editáveis; o formulário trabalha com um projeto por vez.
  useEffect(() => {
    if (prefilledRef.current || loadingOptions || !userData) return;
    const requested = Array.isArray(userData.requested_areas) ? userData.requested_areas : [];
    if (requested.length === 0) return;
    prefilledRef.current = true;

    const matched = requested
      .map((req) => {
        const area = options.guardian_areas.find((a) => a.id_code === req.id_code);
        return area ? { area, role: req.role === 'MANAGER' ? 'MANAGER' : 'TEAM' } : null;
      })
      .filter(Boolean);

    if (matched.length === 0) {
      setSuggestionNote('As áreas sugeridas não estão disponíveis para o seu usuário. Selecione manualmente.');
      return;
    }

    const projectId = areaProjectId(matched[0].area);
    const sameProject = matched
      .filter(({ area }) => String(areaProjectId(area)) === String(projectId))
      .slice(0, MAX_AREAS);
    const skipped = requested.length - sameProject.length;

    setPermissions((prev) => ({
      ...prev,
      project: projectId != null ? String(projectId) : '',
      areas: sameProject.map(({ area, role }) => ({
        id_code: area.id_code,
        name: area.name || '',
        role,
      })),
      selectedArea: '',
    }));
    setSuggestionNote(
      skipped > 0
        ? `${skipped} área(s) sugerida(s) não puderam ser pré-preenchidas (outro projeto ou indisponíveis).`
        : ''
    );
  }, [loadingOptions, userData, options.guardian_areas]);

  const filteredAreas = permissions.project
    ? options.guardian_areas.filter(
      (area) => String(areaProjectId(area)) === String(permissions.project)
    )
    : [];

  const managerReplacements = permissions.areas
    .filter((a) => a.role === 'MANAGER')
    .map((a) => options.guardian_areas.find((area) => area.id_code === a.id_code))
    .filter((area) => area?.adi_responsible_name);

  const isPropTeamInvite = userData?.request_source === 'prop_team';

  const handleBack = () => {
    navigate('/user_request');
  };

  const handleAddArea = () => {
    if (!permissions.selectedArea) return;

    const areaIdCode = permissions.selectedArea;
    if (permissions.areas.some(a => a.id_code === areaIdCode)) return;

    if (permissions.areas.length >= MAX_AREAS) {
      Swal.fire({
        title: 'Limite atingido',
        text: `Você só pode selecionar até ${MAX_AREAS} áreas.`,
        icon: 'warning',
        background: 'var(--surface-overlay)',
        color: 'var(--accent-secondary)',
        confirmButtonColor: 'var(--accent)'
      });
      return;
    }

    const areaObj = filteredAreas.find(a => a.id_code === areaIdCode);

    setPermissions(prev => ({
      ...prev,
      areas: [
        ...prev.areas,
        {
          id_code: areaIdCode,
          name: areaObj?.name || '',
          role: prev.selectedRole
        }
      ],
      selectedArea: ''
    }));
  };

  const handleRemoveArea = (areaIdCode) => {
    setPermissions(prev => ({
      ...prev,
      areas: prev.areas.filter(a => a.id_code !== areaIdCode)
    }));
  };

  const handleSubmit = async () => {
    if (!userData) return;

    if (!permissions.project) {
      Swal.fire({
        title: 'Atenção',
        text: 'Selecione um projeto.',
        icon: 'warning',
        background: 'var(--surface-overlay)',
        color: 'var(--accent-secondary)',
        confirmButtonColor: 'var(--accent)'
      });
      return;
    }

    if (permissions.areas.length === 0) {
      Swal.fire({
        title: 'Atenção',
        text: 'Selecione ao menos uma área e defina a função do usuário nela.',
        icon: 'warning',
        background: 'var(--surface-overlay)',
        color: 'var(--accent-secondary)',
        confirmButtonColor: 'var(--accent)'
      });
      return;
    }

    setLoading(true);
    try {
      const response = await userRequestService.approveUser(
        userData.id,
        {
          areas: permissions.areas.map(a => ({
            id_code: a.id_code,
            role: a.role
          }))
        },
        userType
      );

      if (response.success) {
        Swal.fire({
          title: 'Sucesso!',
          html: `
          <div>
            <p>Usuário aprovado com sucesso!</p>
            ${response.temporary_password ?
              `<p><strong>Senha temporária:</strong> ${response.temporary_password}</p>
               <p style="color: var(--danger); font-size: 0.9rem; margin-top: 10px;">
                 ⚠️ Esta senha será enviada por email ao usuário
               </p>`
              : ''
            }
          </div>
        `,
          icon: 'success',
          background: 'var(--surface-overlay)',
          color: 'var(--accent-secondary)',
          confirmButtonColor: 'var(--accent)'
        }).then(() => {
          navigate('/user_request');
        });
      }
    } catch (error) {
      console.error('Erro completo:', error);
      console.error('Resposta do erro:', error.response?.data);

      let errorMessage = 'Não foi possível aprovar o usuário.';
      if (error.response && error.response.data) {
        errorMessage = error.response.data.error || errorMessage;
        if (error.response.data.details) {
          errorMessage += `\nDetalhes: ${error.response.data.details}`;
        }
      }

      Swal.fire({
        title: 'Erro!',
        text: errorMessage,
        icon: 'error',
        background: 'var(--surface-overlay)',
        color: 'var(--accent-secondary)',
        confirmButtonColor: 'var(--danger)'
      });
    } finally {
      setLoading(false);
    }
  };

  if (!userData) {
    return (
      <MainContainer>
        <BackgroundPattern />
        <ContentWrapper>
          <div style={{ color: 'var(--accent-secondary)', textAlign: 'center' }}>Carregando...</div>
        </ContentWrapper>
      </MainContainer>
    );
  }

  const addressLine = [
    userData.street,
    userData.number,
    userData.complement,
    userData.district,
  ].filter(Boolean).join(', ') || null;

  return (
    <MainContainer>
      <BackgroundPattern />
      <ContentWrapper>
        <ButtonContainerLeft>
          <StyledButton secondary onClick={handleBack}>
            Voltar
          </StyledButton>
        </ButtonContainerLeft>

        <HeaderSection>
          <Title>Aprovar Usuário</Title>
          <Subtitle>Revise os dados, defina o tipo e vincule às áreas do projeto</Subtitle>
        </HeaderSection>

        <UserInfoCard>
          <h2 style={{ color: 'var(--accent-secondary)', marginBottom: '1.5rem' }}>Dados do Usuário</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <StyledLabel>Nome Completo</StyledLabel>
              <InfoText>{displayValue(userData.full_name)}</InfoText>
            </div>
            <div>
              <StyledLabel>Email</StyledLabel>
              <InfoText>{displayValue(userData.email)}</InfoText>
            </div>
            <div>
              <StyledLabel>Telefone</StyledLabel>
              <InfoText>{displayValue(userData.phone)}</InfoText>
            </div>
            <div>
              <StyledLabel>Tipo solicitado</StyledLabel>
              <InfoText>{USER_TYPE_LABELS[userData.user_type] || displayValue(userData.user_type)}</InfoText>
            </div>
            <div>
              <StyledLabel>Status</StyledLabel>
              <InfoText>{displayValue(userData.status)}</InfoText>
            </div>
            <div>
              <StyledLabel>CPF</StyledLabel>
              <InfoText>{displayValue(userData.cpf)}</InfoText>
            </div>
            <div>
              <StyledLabel>CNPJ</StyledLabel>
              <InfoText>{displayValue(userData.cnpj)}</InfoText>
            </div>
            <div>
              <StyledLabel>RG</StyledLabel>
              <InfoText>{displayValue(userData.rg)}</InfoText>
            </div>
            <div>
              <StyledLabel>CEP</StyledLabel>
              <InfoText>{displayValue(userData.cep)}</InfoText>
            </div>
            <div>
              <StyledLabel>Cidade / Estado</StyledLabel>
              <InfoText>
                {displayValue(userData.city)}
                {userData.state ? ` / ${userData.state}` : ''}
              </InfoText>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <StyledLabel>Endereço</StyledLabel>
              <InfoText>{displayValue(addressLine)}</InfoText>
            </div>
            <div>
              <StyledLabel>Termos de uso</StyledLabel>
              <InfoText>{displayValue(userData.accept_terms_of_use)}</InfoText>
            </div>
            <div>
              <StyledLabel>Política de privacidade</StyledLabel>
              <InfoText>{displayValue(userData.accept_privacy_politics)}</InfoText>
            </div>
          </div>
        </UserInfoCard>

        <SettingsCard>
          <h2 style={{ color: 'var(--accent-secondary)', marginBottom: '1.5rem' }}>Configurações de Acesso</h2>

          {isPropTeamInvite && (
            <InfoText style={{ marginBottom: '1rem' }}>
              Convite feito pelo proprietário
              {userData.invited_by_name ? ` ${userData.invited_by_name}` : ''} no app. As áreas
              e funções abaixo foram sugeridas por ele e podem ser alteradas antes da aprovação.
            </InfoText>
          )}
          {suggestionNote && (
            <InfoText style={{ color: 'var(--danger)', marginBottom: '1rem' }}>
              {suggestionNote}
            </InfoText>
          )}

          <Row>
            <LeftColumn>
              <Label>Tipo de usuário</Label>
            </LeftColumn>
            <RightColumn>
              <StyledSelect
                value={userType}
                onChange={(e) => setUserType(e.target.value)}
              >
                <option value="GUARDIAN">Guardian</option>
                <option value="PROP">Proprietário</option>
              </StyledSelect>
            </RightColumn>
          </Row>

          <Row>
            <LeftColumn>
              <Label>Projeto</Label>
            </LeftColumn>
            <RightColumn>
              <StyledSelect
                value={permissions.project}
                disabled={loadingOptions}
                onChange={(e) => setPermissions({
                  ...permissions,
                  project: e.target.value,
                  areas: [],
                  selectedArea: ''
                })}
              >
                <option value="">
                  {loadingOptions ? 'Carregando projetos...' : 'Selecione um projeto'}
                </option>
                {options.projects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.title || project.name || `Projeto #${project.id}`}
                  </option>
                ))}
              </StyledSelect>
              {!loadingOptions && options.projects.length === 0 && (
                <InfoText style={{ color: 'var(--danger)', marginTop: '0.3rem' }}>
                  Nenhum projeto disponível.
                </InfoText>
              )}
            </RightColumn>
          </Row>

          {permissions.project && filteredAreas.length > 0 && (
            <Row>
              <LeftColumn>
                <Label>Área e Função</Label>
              </LeftColumn>
              <RightColumn>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <div>
                    <StyledSelect
                      value={permissions.selectedArea || ''}
                      onChange={(e) => setPermissions({
                        ...permissions,
                        selectedArea: e.target.value
                      })}
                      style={{ width: '100%' }}
                    >
                      <option value="">Selecione uma área</option>
                      {filteredAreas
                        .filter(area => !permissions.areas.some(a => a.id_code === area.id_code))
                        .map(area => (
                          <option key={area.id_code} value={area.id_code}>
                            {area.name}
                          </option>
                        ))}
                    </StyledSelect>
                  </div>

                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <StyledSelect
                      value={permissions.selectedRole}
                      onChange={(e) => setPermissions({
                        ...permissions,
                        selectedRole: e.target.value
                      })}
                      style={{ flex: 1, maxWidth: '200px' }}
                    >
                      <option value="TEAM">Equipe</option>
                      <option value="MANAGER">Gestor</option>
                    </StyledSelect>

                    <StyledButton
                      onClick={handleAddArea}
                      disabled={!permissions.selectedArea}
                      style={{ minWidth: 'auto', padding: '8px 16px' }}
                    >
                      Adicionar
                    </StyledButton>
                  </div>

                  {permissions.areas.length > 0 && (
                    <TagsContainer style={{ marginTop: '0.5rem' }}>
                      {permissions.areas.map(areaEntry => (
                        <Tag key={`selected-area-${areaEntry.id_code}`}>
                          {areaEntry.name} — {ROLE_LABELS[areaEntry.role]}
                          <RemoveTagButton
                            onClick={() => handleRemoveArea(areaEntry.id_code)}
                          >
                            ×
                          </RemoveTagButton>
                        </Tag>
                      ))}
                    </TagsContainer>
                  )}

                  {managerReplacements.length > 0 && (
                    <InfoText style={{ color: 'var(--danger)', marginTop: '0.5rem' }}>
                      Ao aprovar como Gestor, o gestor atual será substituído em:{' '}
                      {managerReplacements
                        .map((area) => `${area.name} (${area.adi_responsible_name})`)
                        .join(', ')}
                      .
                    </InfoText>
                  )}
                </div>
              </RightColumn>
            </Row>
          )}

          {permissions.project && !loadingOptions && filteredAreas.length === 0 && (
            <Row>
              <LeftColumn>
                <Label>Área(s)</Label>
              </LeftColumn>
              <RightColumn>
                <InfoText style={{ color: 'var(--danger)' }}>
                  Nenhuma área encontrada para este projeto.
                </InfoText>
              </RightColumn>
            </Row>
          )}
        </SettingsCard>

        <ButtonGroup>
          <StyledButton secondary onClick={handleBack} disabled={loading}>
            Cancelar
          </StyledButton>
          <StyledButton onClick={handleSubmit} disabled={loading || loadingOptions}>
            {loading ? 'Aprovando...' : 'Confirmar Aprovação'}
          </StyledButton>
        </ButtonGroup>
      </ContentWrapper>
    </MainContainer >
  );
};

export default AddUserRequest;
